import type {ReactElement} from 'react';
import type {Component} from 'grapesjs';
import {Text, TextSize} from '@Components/text/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {setComponentStyleProperty} from '@FormMakerReact/lib/form-maker-style-library';
import {useComponentStyleProperty} from '@FormMakerReact/lib/form-maker-style-hooks';
import controlStyles from '@FormMakerReact/layout/form-maker-right-panel/controls/control-row.module.scss';
import styles from './text-alignment-control.module.scss';
import type {AlignmentOption, TextAlignmentControlProps} from './text-alignment-control.types';

const TEXT_ALIGN_PROPERTY = 'text-align';
const DEFAULT_TEXT_ALIGN = 'center';
const DEFAULT_LABEL_KEY = 'pmwjs_form_maker_control_alignment_label';

const ALIGNMENT_OPTIONS: readonly AlignmentOption[] = [
  {value: 'left', icon: 'icon-align-left'},
  {value: 'center', icon: 'icon-align-center'},
  {value: 'right', icon: 'icon-align-right'},
];

export function TextAlignmentControl({component, labelKey = DEFAULT_LABEL_KEY}: TextAlignmentControlProps): ReactElement {
  const rawAlignment = useComponentStyleProperty(component, TEXT_ALIGN_PROPERTY);
  const currentAlignment = rawAlignment === '' ? DEFAULT_TEXT_ALIGN : rawAlignment;
  return (
    <div className={controlStyles.row}>
      <Text val={window.i18next.t(labelKey)} size={TextSize.MD} className={controlStyles.label} />
      <div className={styles.controls}>{ALIGNMENT_OPTIONS.map((option) => renderAlignmentIcon(component, option, currentAlignment))}</div>
    </div>
  );
}

function renderAlignmentIcon(component: Component, option: AlignmentOption, currentAlignment: string): ReactElement {
  const isSelected = currentAlignment === option.value;
  return (
    <Icon
      key={option.value}
      icon={option.icon}
      shape={IconShape.SQUARE}
      type={IconType.TRANSPARENT}
      className={isSelected ? styles.alignmentIconSelected : undefined}
      size={IconSize.SIZE_ICON_16}
      ariaLabel={window.i18next.t(option.value)}
      isUsedAsButton
      onClick={(): void => {
        setComponentStyleProperty(component, TEXT_ALIGN_PROPERTY, option.value);
      }}
    />
  );
}
